import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import PageHero from '../../components/ui/PageHero';
import PageCTA from '../../components/ui/PageCTA';
import SectionHeader from '../../components/ui/SectionHeader';
import GlowCard from '../../components/ui/GlowCard';
import Section from '../../components/ui/Section';
import BrandIcon from '../../components/ui/BrandIcon';
import StatsBar from '../../components/home/StatsBar';
import { offices } from '../../data/contact';

const Split = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StoryText = styled.div`
  p {
    color: #64748b;
    font-size: 0.95rem;
    line-height: 1.8;
    margin-bottom: 16px;
  }
`;

const HighlightPanel = styled.div`
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #e8eef3;
  box-shadow: 0 12px 40px rgba(14, 107, 60, 0.1);
`;

const PanelTop = styled.div`
  padding: 32px 28px;
  background: linear-gradient(145deg, #0e6b3c 0%, #0a5a32 55%, #15803d 100%);
  color: #fff;

  h3 {
    font-family: var(--font-display);
    font-size: 1.35rem;
    font-weight: 800;
    margin-bottom: 8px;
  }

  > p {
    font-size: 0.88rem;
    opacity: 0.9;
    margin-bottom: 20px;
  }
`;

const CheckList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.86rem;
    padding: 8px 0;
    border-top: 1px solid rgba(255, 255, 255, 0.12);

    i {
      color: #f2b705;
      font-size: 0.75rem;
    }
  }
`;

const PanelBottom = styled.div`
  padding: 24px 28px;
  background: #fff;

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  span {
    font-size: 0.78rem;
    font-weight: 600;
    padding: 6px 14px;
    border-radius: 100px;
    background: rgba(14, 107, 60, 0.08);
    border: 1px solid rgba(14, 107, 60, 0.15);
    color: #0e6b3c;
    font-family: var(--font-display);
  }
`;

const MissionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MissionCard = styled(GlowCard)`
  height: 100%;
  border-left: ${(p) => (p.$accent ? '4px solid #0e6b3c' : '4px solid #6dbb1a')};

  h3 {
    font-family: var(--font-display);
    font-size: 1.1rem;
    font-weight: 700;
    color: #0f172a;
    margin: 14px 0 10px;
    text-align: left;
  }

  p {
    font-size: 0.9rem;
    color: #64748b;
    line-height: 1.7;
    text-align: left;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled(motion.div)`
  padding: 28px 22px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e8eef3;
  text-align: center;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 40px rgba(14, 107, 60, 0.1);
  }

  h3 {
    font-family: var(--font-display);
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    margin: 14px 0 8px;
  }

  p {
    font-size: 0.84rem;
    color: #64748b;
    line-height: 1.6;
  }
`;

const PresenceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  max-width: 720px;
  margin: 0 auto;

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
  }
`;

const OfficeCard = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e8eef3;
  text-align: center;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);

  .flag {
    font-size: 1.75rem;
    margin-bottom: 10px;
  }

  h4 {
    font-family: var(--font-display);
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 8px;
  }

  p {
    font-size: 0.84rem;
    color: #64748b;
    line-height: 1.55;
    margin-bottom: 10px;
  }

  a {
    font-size: 0.85rem;
    font-weight: 600;
    color: #0e6b3c;
    text-decoration: none;

    &:hover {
      color: #6dbb1a;
    }
  }
`;

const ExploreRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 8px;
`;

const ExploreBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 100px;
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 0.88rem;
  text-decoration: none;
  border: 1px solid #e2e8f0;
  color: #0e6b3c;
  background: #fff;

  &:hover {
    border-color: #0e6b3c;
    background: #f0faf4;
  }

  &.primary {
    background: #0e6b3c;
    color: #fff;
    border-color: transparent;
  }
`;

const values = [
  { icon: 'fas fa-lightbulb', title: 'Innovation', description: 'Modern stacks and smart architecture for lasting products.' },
  { icon: 'fas fa-award', title: 'Excellence', description: 'Clean code, thorough QA, and polished user experiences.' },
  { icon: 'fas fa-handshake', title: 'Integrity', description: 'Honest timelines, clear pricing, and transparent communication.' },
  { icon: 'fas fa-users', title: 'Partnership', description: 'We work with you as an extension of your team.' },
];

const strengths = [
  { icon: 'fas fa-code', title: 'Full-Stack Expertise', description: 'Web, mobile, CRM, ERP, and cloud under one roof.' },
  { icon: 'fas fa-globe', title: 'India & UK Presence', description: 'Local support with global delivery standards.' },
  { icon: 'fas fa-bolt', title: 'Agile Delivery', description: 'Fast iterations with regular demos and feedback.' },
  { icon: 'fas fa-headset', title: 'Dedicated Support', description: 'Post-launch maintenance and long-term partnership.' },
];

const highlights = [
  'Custom websites & web applications',
  'CRM, ERP & business automation',
  'E-commerce & WooCommerce stores',
  'UI/UX design & digital marketing',
];

const officeFlags = { india: '🇮🇳', uk: '🇬🇧' };

const About = () => {
  const [storyRef, storyInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [valuesRef, valuesInView] = useInView({ triggerOnce: true, threshold: 0.08 });

  return (
    <div>
      <PageHero
        label="About Us"
        title="About Rhutech Solutions"
        titleHighlight="Rhutech"
        description="A trusted software partner helping businesses in India and the UK build digital products that scale."
        breadcrumb="About"
      />

      <Section ref={storyRef}>
        <div className="container">
          <Split>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={storyInView ? { opacity: 1, y: 0 } : {}}>
              <SectionHeader
                label="Our Story"
                title="Building Software That Matters"
                align="left"
                subtitle="Since 2020, we've helped startups and enterprises turn ideas into reliable digital solutions."
              />
              <StoryText>
                <p>
                  Rhutech Solutions was founded with a simple goal: deliver enterprise-quality software at a
                  pace and price that growing businesses can afford. From landing pages to full CRM and ERP
                  platforms, we cover the entire product lifecycle.
                </p>
                <p>
                  Our team combines strong engineering with clear communication — so you always know where
                  your project stands and what comes next.
                </p>
              </StoryText>
              <ExploreRow>
                <ExploreBtn className="primary" to="/contact">
                  Work With Us <i className="fas fa-arrow-right" />
                </ExploreBtn>
                <ExploreBtn to="/projects">View Our Work</ExploreBtn>
              </ExploreRow>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={storyInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 }}
            >
              <HighlightPanel>
                <PanelTop>
                  <h3>What We Deliver</h3>
                  <p>End-to-end digital services for modern businesses</p>
                  <CheckList>
                    {highlights.map((item) => (
                      <li key={item}>
                        <i className="fas fa-check-circle" />
                        {item}
                      </li>
                    ))}
                  </CheckList>
                </PanelTop>
                <PanelBottom>
                  <div className="tags">
                    <span>React & Node.js</span>
                    <span>Laravel & PHP</span>
                    <span>WordPress</span>
                    <span>Cloud & DevOps</span>
                  </div>
                </PanelBottom>
              </HighlightPanel>
            </motion.div>
          </Split>
        </div>
      </Section>

      <StatsBar />

      <Section $alt>
        <div className="container">
          <SectionHeader label="Purpose" title="Mission & Vision" />
          <MissionGrid>
            <MissionCard $accent padding="28px 26px">
              <BrandIcon><i className="fas fa-bullseye" /></BrandIcon>
              <h3>Our Mission</h3>
              <p>
                Empower businesses with custom software that improves efficiency, reaches more customers,
                and drives measurable growth — without unnecessary complexity.
              </p>
            </MissionCard>
            <MissionCard padding="28px 26px">
              <BrandIcon><i className="fas fa-eye" /></BrandIcon>
              <h3>Our Vision</h3>
              <p>
                To be the most trusted development partner for SMEs in India and the UK — known for quality,
                transparency, and long-term client relationships.
              </p>
            </MissionCard>
          </MissionGrid>
        </div>
      </Section>

      <Section ref={valuesRef}>
        <div className="container">
          <SectionHeader label="Values" title="Core Values" subtitle="The principles behind every project we deliver" />
          <CardGrid>
            {values.map((v, i) => (
              <ValueCard
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.06 }}
              >
                <BrandIcon style={{ margin: '0 auto' }}>
                  <i className={v.icon} />
                </BrandIcon>
                <h3>{v.title}</h3>
                <p>{v.description}</p>
              </ValueCard>
            ))}
          </CardGrid>
        </div>
      </Section>

      <Section $alt>
        <div className="container">
          <SectionHeader label="Why Us" title="Why Choose Rhutech?" titleHighlight="Rhutech" />
          <CardGrid>
            {strengths.map((s, i) => (
              <ValueCard
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <BrandIcon style={{ margin: '0 auto' }}>
                  <i className={s.icon} />
                </BrandIcon>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
              </ValueCard>
            ))}
          </CardGrid>
        </div>
      </Section>

      <Section>
        <div className="container">
          <SectionHeader label="Global Reach" title="Offices & Support" subtitle="Serving clients across India and the United Kingdom" />
          <PresenceGrid>
            {offices.map((office) => (
              <OfficeCard key={office.id}>
                <div className="flag">{officeFlags[office.id]}</div>
                <h4>{office.title}</h4>
                <p>
                  {office.lines.map((line, idx) => (
                    <React.Fragment key={line}>
                      {line}
                      {idx < office.lines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </p>
                <a href={office.phoneHref}>{office.phone}</a>
              </OfficeCard>
            ))}
          </PresenceGrid>
        </div>
      </Section>

      <PageCTA
        title="Ready To Work With Us?"
        description="Tell us about your project — we'll respond within 24 hours with a free consultation."
        primaryLabel="Get Free Consultation"
        secondaryLabel="View Services"
        secondaryTo="/services"
      />
    </div>
  );
};

export default About;
